import React, { useState } from 'react';
import ReactModal from 'react-modal';
import Select from 'react-select';
import notifications from '../../notifications';
import { updateUser } from '../../api/admin';
import './StaffEditModal.css';

const StaffEditModal = ({
  staff,
  sportOptions,
  seasonOptions,
  ageRangeOptions,
  userTypeOptions,
  statusOptions,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    firstName: staff.firstName || '',
    lastName: staff.lastName || '',
    coachName: staff.coachName || '',
    email: staff.email || '',
    phoneNumber: staff.phoneNumber || '',
    userType: staff.userType || 'coach',
    status: staff.status || 'active',
    hourlyRate: staff.hourlyRate || '',
    // Payroll fields
    ssn: staff.ssn || '',
    birthdate: staff.birthdate || '',
    address1: staff.address1 || '',
    city: staff.city || '',
    state: staff.state || '',
    zipCode: staff.zipCode || '',
    // Preferences
    preferredSports: staff.preferredSports || [],
    preferredAgeRange: staff.preferredAgeRange || [],
    preferredShifts: staff.preferredShifts || [],
    avoidSports: staff.avoidSports || [],
    avoidAgeRange: staff.avoidAgeRange || [],
    avoidShifts: staff.avoidShifts || [],
    availableSeasons: staff.availableSeasons || []
  });
  const [isLoading, setIsLoading] = useState(false);

  const shiftOptions = [
    { value: 'morning', label: 'Morning' },
    { value: 'afternoon', label: 'Afternoon' },
    { value: 'evening', label: 'Evening' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name) => (option) => {
    setFormData(prev => ({
      ...prev,
      [name]: option?.value || ''
    }));
  };

  const handleMultiSelectChange = (name) => (options) => {
    setFormData(prev => ({
      ...prev,
      [name]: options ? options.map(o => o.value) : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userData = {
        ...formData,
        hourlyRate: formData.hourlyRate ? parseFloat(Number(formData.hourlyRate).toFixed(2)) : null
      };

      const response = await updateUser(staff.id, userData);
      if (!response.error) {
        notifications.notifySuccess('Staff member updated successfully');
        onSuccess({
          ...staff,
          ...formData,
          hourlyRate: formData.hourlyRate ? parseFloat(Number(formData.hourlyRate).toFixed(2)) : null
        });
      } else {
        notifications.notifyError('Failed to update staff member');
      }
    } catch (error) {
      notifications.notifyError('An error occurred while updating staff member');
    } finally {
      setIsLoading(false);
    }
  };

  const findOption = (options, value) => options.find(o => o.value === value) || null;
  const findMultiOptions = (options, values) => options.filter(o => values?.includes(o.value));

  return (
    <ReactModal
      isOpen={true}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={true}
      contentLabel="Edit Staff"
      ariaHideApp={false}
      className="staff-edit-modal-content"
      overlayClassName="modal-overlay"
    >
      <div className="staff-edit-modal">
        <button className="close-button" onClick={onClose}>×</button>
        <h2>Edit Staff Member</h2>

        <form onSubmit={handleSubmit} className="staff-edit-form">
          <div className="form-section">
            <h3>Basic Info</h3>
            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Coach Name</label>
                <input
                  type="text"
                  name="coachName"
                  value={formData.coachName}
                  onChange={handleChange}
                  placeholder="Display name for parents/kids"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="(555) 555-5555"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Type</label>
                <Select
                  value={findOption(userTypeOptions, formData.userType)}
                  onChange={handleSelectChange('userType')}
                  options={userTypeOptions}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <Select
                  value={findOption(statusOptions, formData.status)}
                  onChange={handleSelectChange('status')}
                  options={statusOptions}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Hourly Rate ($)</label>
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Payroll Info</h3>
            <div className="form-row">
              <div className="form-group">
                <label>SSN/ITIN</label>
                <input
                  type="text"
                  name="ssn"
                  value={formData.ssn}
                  onChange={handleChange}
                  placeholder="XXX-XX-XXXX"
                />
              </div>
              <div className="form-group">
                <label>Birthdate</label>
                <input
                  type="date"
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="address1"
                  value={formData.address1}
                  onChange={handleChange}
                  placeholder="Street address"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="e.g. San Mateo"
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="e.g. CA"
                />
              </div>
              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  placeholder="e.g. 94010"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Preferences</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Preferred Sports</label>
                <Select
                  value={findMultiOptions(sportOptions, formData.preferredSports)}
                  onChange={handleMultiSelectChange('preferredSports')}
                  options={sportOptions}
                  isMulti
                  placeholder="Select sports..."
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Preferred Age Range</label>
                <Select
                  value={findMultiOptions(ageRangeOptions, formData.preferredAgeRange)}
                  onChange={handleMultiSelectChange('preferredAgeRange')}
                  options={ageRangeOptions}
                  isMulti
                  placeholder="Select age ranges..."
                />
              </div>
              <div className="form-group">
                <label>Preferred Shifts</label>
                <Select
                  value={findMultiOptions(shiftOptions, formData.preferredShifts)}
                  onChange={handleMultiSelectChange('preferredShifts')}
                  options={shiftOptions}
                  isMulti
                  placeholder="Select shifts..."
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Available Seasons</label>
                <Select
                  value={findMultiOptions(seasonOptions, formData.availableSeasons)}
                  onChange={handleMultiSelectChange('availableSeasons')}
                  options={seasonOptions}
                  isMulti
                  placeholder="Select seasons..."
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Avoid</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Avoid Sports</label>
                <Select
                  value={findMultiOptions(sportOptions, formData.avoidSports)}
                  onChange={handleMultiSelectChange('avoidSports')}
                  options={sportOptions}
                  isMulti
                  placeholder="Select sports to avoid..."
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Avoid Age Range</label>
                <Select
                  value={findMultiOptions(ageRangeOptions, formData.avoidAgeRange)}
                  onChange={handleMultiSelectChange('avoidAgeRange')}
                  options={ageRangeOptions}
                  isMulti
                  placeholder="Select age ranges to avoid..."
                />
              </div>
              <div className="form-group">
                <label>Avoid Shifts</label>
                <Select
                  value={findMultiOptions(shiftOptions, formData.avoidShifts)}
                  onChange={handleMultiSelectChange('avoidShifts')}
                  options={shiftOptions}
                  isMulti
                  placeholder="Select shifts to avoid..."
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </ReactModal>
  );
};

export default StaffEditModal;
